import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, Bell, Shield, LogOut, ArrowLeft, 
  Eye, EyeOff, Save, Trash2, Download,
  Mail, Smartphone, Building, CheckCircle, Key
} from 'lucide-react';
import toast from 'react-hot-toast';
import ModernHeader from '../components/ModernHeader';

interface ConfiguracoesProps {
  userType: 'cliente' | 'fornecedor';
}

const Configuracoes: React.FC<ConfiguracoesProps> = ({ userType }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('perfil');
  const [formData, setFormData] = useState({
    nome: 'João Silva',
    email: 'joao@exemplo.com',
    telefone: '(11) 99999-9999',
    empresa: userType === 'cliente' ? 'Empresa ABC Ltda' : 'Peças Express Ltda',
    cnpj: userType === 'fornecedor' ? '12.345.678/0001-90' : '',
    endereco: 'Rua Exemplo, 123 - São Paulo, SP',
    senha_atual: '',
    nova_senha: '',
    confirmar_senha: ''
  });

  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    sms: false,
    whatsapp: true
  });

  const handleSaveProfile = () => {
    toast.success('Perfil atualizado com sucesso!');
  };

  const handleChangePassword = () => {
    if (formData.nova_senha !== formData.confirmar_senha) {
      toast.error('As senhas não coincidem');
      return;
    }
    if (formData.nova_senha.length < 6) {
      toast.error('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }
    toast.success('Senha alterada com sucesso!');
    setFormData(prev => ({ ...prev, senha_atual: '', nova_senha: '', confirmar_senha: '' }));
  };

  const handleSaveNotifications = () => {
    toast.success('Configurações de notificação salvas!');
  };

  const handleLogout = () => {
    toast.success('Logout realizado com sucesso!');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo-pecaja.png" 
                alt="PeçaJá & Sobratema" 
                className="h-12 w-auto object-contain"
              />
            </div>

            <div className="flex items-center space-x-4">
              <button onClick={handleLogout} className="btn btn-outline">
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container py-8">
        {/* Breadcrumb */}
        <div className="breadcrumb mb-6">
          <button
            onClick={() => navigate(-1)}
            className="breadcrumb-item"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Voltar
          </button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Configurações</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurações</h3>
              <nav className="space-y-2">
                <button
                  onClick={() => setActiveTab('perfil')}
                  className={`w-full text-left px-6 py-4 rounded-lg transition-colors border ${
                    activeTab === 'perfil' 
                      ? 'bg-red text-white border-red' 
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                  }`}
                  style={{ padding: '15px' }}
                >
                  <div className="flex items-center space-x-3">
                    <User className="w-5 h-5" />
                    <span className="font-medium">Perfil</span>
                  </div>
                </button>
                
                <button
                  onClick={() => setActiveTab('notificacoes')}
                  className={`w-full text-left px-6 py-4 rounded-lg transition-colors border ${
                    activeTab === 'notificacoes' 
                      ? 'bg-red text-white border-red' 
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                  }`}
                  style={{ padding: '15px' }}
                >
                  <div className="flex items-center space-x-3">
                    <Bell className="w-5 h-5" />
                    <span className="font-medium">Notificações</span>
                  </div>
                </button>
                
                <button
                  onClick={() => setActiveTab('seguranca')}
                  className={`w-full text-left px-6 py-4 rounded-lg transition-colors border ${
                    activeTab === 'seguranca' 
                      ? 'bg-red text-white border-red' 
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                  }`}
                  style={{ padding: '15px' }}
                >
                  <div className="flex items-center space-x-3">
                    <Shield className="w-5 h-5" />
                    <span className="font-medium">Segurança</span>
                  </div>
                </button>
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-2">
            {activeTab === 'perfil' && (
              <div className="card">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Informações do Perfil</h2>
                    <p className="text-gray-600">Atualize suas informações pessoais</p>
                  </div>
                  <button
                    onClick={handleSaveProfile}
                    className="btn btn-primary"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Salvar Alterações
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">Nome Completo</label>
                    <div className="flex items-center space-x-2">
                      <User className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <input
                        type="text"
                        value={formData.nome}
                        onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                        className="form-input flex-1"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="form-label">E-mail</label>
                    <div className="flex items-center space-x-2">
                      <Mail className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="form-input flex-1"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="form-label">Telefone</label>
                    <div className="flex items-center space-x-2">
                      <Smartphone className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <input
                        type="tel"
                        value={formData.telefone}
                        onChange={(e) => setFormData(prev => ({ ...prev, telefone: e.target.value }))}
                        className="form-input flex-1"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="form-label">{userType === 'cliente' ? 'Empresa' : 'Nome da Empresa'}</label>
                    <div className="flex items-center space-x-2">
                      <Building className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <input
                        type="text"
                        value={formData.empresa}
                        onChange={(e) => setFormData(prev => ({ ...prev, empresa: e.target.value }))}
                        className="form-input flex-1"
                      />
                    </div>
                  </div>

                  {userType === 'fornecedor' && (
                    <div>
                      <label className="form-label">CNPJ</label>
                      <input
                        type="text"
                        value={formData.cnpj}
                        onChange={(e) => setFormData(prev => ({ ...prev, cnpj: e.target.value }))}
                        className="form-input flex-1"
                      />
                    </div>
                  )}

                  <div className="md:col-span-2">
                    <label className="form-label">Endereço</label>
                    <input
                      type="text"
                      value={formData.endereco}
                      onChange={(e) => setFormData(prev => ({ ...prev, endereco: e.target.value }))}
                      className="form-input flex-1"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'notificacoes' && (
              <div className="card">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Configurações de Notificação</h2>
                    <p className="text-gray-600">Gerencie como você recebe notificações</p>
                  </div>
                  <button
                    onClick={handleSaveNotifications}
                    className="btn btn-primary"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Salvar Configurações
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">Notificações por E-mail</h4>
                      <p className="text-sm text-gray-600">Receba atualizações por e-mail</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifications.email}
                        onChange={(e) => setNotifications(prev => ({ ...prev, email: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">Notificações Push</h4>
                      <p className="text-sm text-gray-600">Receba notificações no navegador</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifications.push}
                        onChange={(e) => setNotifications(prev => ({ ...prev, push: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">Notificações por SMS</h4>
                      <p className="text-sm text-gray-600">Receba notificações por SMS</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifications.sms}
                        onChange={(e) => setNotifications(prev => ({ ...prev, sms: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">Notificações WhatsApp</h4>
                      <p className="text-sm text-gray-600">Receba notificações pelo WhatsApp</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifications.whatsapp}
                        onChange={(e) => setNotifications(prev => ({ ...prev, whatsapp: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red"></div>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'seguranca' && (
              <div className="card">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Alterar Senha</h2>
                    <p className="text-gray-600">Mantenha sua conta segura</p>
                  </div>
                  <button
                    onClick={handleChangePassword}
                    className="btn btn-primary"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Alterar Senha
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="form-label">Senha Atual</label>
                    <div className="flex items-center space-x-2">
                      <Key className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <input
                        type="password"
                        value={formData.senha_atual}
                        onChange={(e) => setFormData(prev => ({ ...prev, senha_atual: e.target.value }))}
                        className="form-input flex-1"
                        placeholder="Digite sua senha atual"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="form-label">Nova Senha</label>
                    <div className="flex items-center space-x-2">
                      <Key className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <input
                        type="password"
                        value={formData.nova_senha}
                        onChange={(e) => setFormData(prev => ({ ...prev, nova_senha: e.target.value }))}
                        className="form-input flex-1"
                        placeholder="Digite a nova senha"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="form-label">Confirmar Nova Senha</label>
                    <div className="flex items-center space-x-2">
                      <Key className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <input
                        type="password"
                        value={formData.confirmar_senha}
                        onChange={(e) => setFormData(prev => ({ ...prev, confirmar_senha: e.target.value }))}
                        className="form-input flex-1"
                        placeholder="Confirme a nova senha"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Configuracoes; 